<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import { api } from "../api/client";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { locale, t, type MessageKey } from "../i18n";

/**
 * The contact in the connected CRM (OutboundPro or Pipedrive): the person, their open deals with
 * stage and value, and links into the CRM. Shown only when a CRM is connected.
 */
const props = defineProps<{ slug: string; contactId: string }>();

interface Card {
  connector: {
    kind: "outboundpro" | "pipedrive";
    account: string | null;
  } | null;
  person?: { id: number; name?: string; url: string | null } | null;
  deals?: Array<{
    id: number;
    title: string;
    value: number | null;
    currency: string | null;
    stage: string | null;
    url: string | null;
  }>;
  error?: string;
}
const card = ref<Card | null>(null);
const busy = ref(false);
const crmName = computed(() =>
  card.value?.connector
    ? t(`crm.kind.${card.value.connector.kind}` as MessageKey)
    : "",
);

async function load() {
  try {
    card.value = await api<Card>(
      `/v1/c/${props.slug}/contacts/${props.contactId}/crm`,
    );
  } catch {
    card.value = null;
  }
}
onMounted(load);
watch(() => props.contactId, load);

async function create() {
  busy.value = true;
  try {
    card.value = await api<Card>(
      `/v1/c/${props.slug}/contacts/${props.contactId}/crm`,
      {
        method: "POST",
        body: {},
      },
    );
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
const money = (value: number | null, currency: string | null) =>
  value === null
    ? ""
    : new Intl.NumberFormat(locale.value, {
        style: "currency",
        currency: currency ?? "BRL",
        maximumFractionDigits: 0,
      }).format(value);
</script>
<template>
  <section v-if="card?.connector" class="detail-section crm-panel">
    <h3 class="contact-section-title">
      {{ t("crm.card.title", { crm: crmName }) }}
    </h3>
    <p v-if="card.error" class="small muted">
      {{ t("crm.card.error", { crm: crmName }) }}
    </p>
    <template v-else-if="card.person">
      <a
        v-if="card.person.url"
        class="list-row detail-section-link"
        :href="card.person.url"
        target="_blank"
        rel="noopener"
      >
        <Icon name="user" /><span class="grow">{{
          card.person.name || t("crm.card.open", { crm: crmName })
        }}</span
        ><Icon name="external" :size="18" />
      </a>
      <small class="muted">{{ t("crm.card.deals") }}</small>
      <p v-if="!card.deals?.length" class="small muted">
        {{ t("crm.card.no_deals") }}
      </p>
      <a
        v-for="d in card.deals"
        :key="d.id"
        class="crm-deal"
        :href="d.url ?? undefined"
        target="_blank"
        rel="noopener"
      >
        <strong>{{ d.title }}</strong>
        <small class="muted"
          >{{ d.stage ?? ""
          }}<template v-if="d.stage && d.value !== null"> · </template
          >{{ money(d.value, d.currency) }}</small
        >
      </a>
    </template>
    <template v-else>
      <p class="small muted">{{ t("crm.card.none", { crm: crmName }) }}</p>
      <Button variant="secondary" compact :loading="busy" @click="create">{{
        t("crm.card.create", { crm: crmName })
      }}</Button>
    </template>
  </section>
</template>
